import React, { useState, useEffect } from "react";
import "./SearchFeed.css";
import SearchBar from "./SearchBar";
import SearchResults from "./SearchResults";
import SearchHistory from "./SearchHistory";
import SearchAnalytics from "./SearchAnalytics";
import SearchService from "../../services/SearchService";

/**
 * SearchFeed Component
 * Componente principal para gerenciar todo o sistema de pesquisas
 * @version 1.0.0
 */

const SearchFeed = () => {
  const [activeTab, setActiveTab] = useState("search"); // search, history, saved, trending, analytics
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchHistory, setSearchHistory] = useState([]);
  const [savedSearches] = useState([]);
  const [trendingSearches, setTrendingSearches] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [filters, setFilters] = useState({
    category: "",
    difficulty: "",
    dateFrom: "",
    dateTo: "",
  });

  // Carregar histórico ao montar o componente
  useEffect(() => {
    loadSearchHistory();
    loadTrendingSearches();
    loadUserStats();
  }, []);

  // Função para carregar histórico
  const loadSearchHistory = async () => {
    try {
      const history = await SearchService.getSearchHistory(10);
      setSearchHistory(history);
    } catch (err) {
      console.error("Erro ao carregar histórico:", err);
    }
  };

  // Função para carregar buscas em trending
  const loadTrendingSearches = async () => {
    try {
      const trending = await SearchService.getTrendingSearches(10);
      setTrendingSearches(trending);
    } catch (err) {
      console.error("Erro ao carregar trending:", err);
    }
  };

  // Função para carregar estatísticas do usuário
  const loadUserStats = async () => {
    try {
      const stats = await SearchService.getUserSearchStats();
      setUserStats(stats);
    } catch (err) {
      console.error("Erro ao carregar estatísticas:", err);
    }
  };

  // Função de busca global
  const handleGlobalSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults(null);
      setError(null);
      return;
    }

    setSearchQuery(query);
    setLoading(true);
    setError(null);
    setActiveTab("results");

    try {
      const results = await SearchService.globalSearch(query, 20, 0);
      setSearchResults(SearchService.formatResults(results));
    } catch (err) {
      setError("Erro ao realizar a busca. Tente novamente.");
      console.error("Erro na busca:", err);
    } finally {
      setLoading(false);
    }
  };

  // Função de busca avançada
  const handleAdvancedSearch = async (query) => {
    if (!query.trim()) return;

    setSearchQuery(query);
    setLoading(true);
    setError(null);
    setActiveTab("results");

    try {
      const results = await SearchService.advancedSearch(
        query,
        filters,
        ["exercise", "user", "blog", "lookbook"]
      );
      setSearchResults(SearchService.formatResults(results));
    } catch (err) {
      setError("Erro ao realizar busca avançada.");
      console.error("Erro na busca avançada:", err);
    } finally {
      setLoading(false);
    }
  };

  // Função para limpar histórico
  const handleClearHistory = async () => {
    if (window.confirm("Tem certeza que deseja limpar o histórico?")) {
      try {
        await SearchService.clearSearchHistory(true);
        setSearchHistory([]);
      } catch (err) {
        setError("Erro ao limpar histórico.");
        console.error("Erro ao limpar:", err);
      }
    }
  };

  return (
    <div className="search-feed">
      {/* Header */}
      <div className="search-feed-header">
        <div className="search-feed-title">
          <h1>🔍 Pesquisas Avançadas</h1>
          <p>Encontre exercícios, usuários, blogs e muito mais</p>
        </div>
      </div>

      {/* Barra de Pesquisa */}
      <SearchBar onSearch={handleGlobalSearch} />

      {/* Tabs de Navegação */}
      <div className="search-tabs">
        <button
          className={`tab-btn ${activeTab === "search" ? "active" : ""}`}
          onClick={() => setActiveTab("search")}
        >
          🔍 Buscar
        </button>
        <button
          className={`tab-btn ${activeTab === "history" ? "active" : ""}`}
          onClick={() => setActiveTab("history")}
        >
          ⏱️ Histórico
        </button>
        <button
          className={`tab-btn ${activeTab === "saved" ? "active" : ""}`}
          onClick={() => setActiveTab("saved")}
        >
          💾 Salvos
        </button>
        <button
          className={`tab-btn ${activeTab === "trending" ? "active" : ""}`}
          onClick={() => setActiveTab("trending")}
        >
          🔥 Trending
        </button>
        <button
          className={`tab-btn ${activeTab === "analytics" ? "active" : ""}`}
          onClick={() => setActiveTab("analytics")}
        >
          📊 Análise
        </button>
      </div>

      {/* Conteúdo Ativo */}
      <div className="search-content">
        {/* Aba de Busca */}
        {activeTab === "search" && (
          <div className="search-panel">
            <div className="filters-panel">
              <h3>Filtros Avançados</h3>
              <div className="filter-group">
                <label>Categoria:</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="all">Todas</option>
                  <option value="exercise">Exercícios</option>
                  <option value="user">Usuários</option>
                  <option value="blog">Blogs</option>
                  <option value="lookbook">Lookbooks</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Dificuldade:</label>
                <select
                  value={filters.difficulty}
                  onChange={(e) =>
                    setFilters({ ...filters, difficulty: e.target.value })
                  }
                >
                  <option value="">Todas</option>
                  <option value="beginner">Iniciante</option>
                  <option value="intermediate">Intermediário</option>
                  <option value="advanced">Avançado</option>
                </select>
              </div>

              <div className="filter-group">
                <label>De:</label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) =>
                    setFilters({ ...filters, dateFrom: e.target.value })
                  }
                />
              </div>

              <div className="filter-group">
                <label>Até:</label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) =>
                    setFilters({ ...filters, dateTo: e.target.value })
                  }
                />
              </div>

              <button
                className="btn-search-advanced"
                onClick={() => handleAdvancedSearch(searchQuery)}
              >
                Busca Avançada
              </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            {loading && <div className="loading">Pesquisando...</div>}

            {searchResults && !loading && (
              <SearchResults results={searchResults} query={searchQuery} />
            )}

            {!searchResults && !loading && !error && (
              <div className="search-empty">
                <p>👆 Comece digitando na barra de pesquisa acima</p>
              </div>
            )}
          </div>
        )}

        {/* Aba de Histórico */}
        {activeTab === "history" && (
          <SearchHistory
            history={searchHistory}
            onClearHistory={handleClearHistory}
            onSearch={handleGlobalSearch}
          />
        )}

        {/* Aba de Pesquisas Salvas */}
        {activeTab === "saved" && (
          <div className="saved-searches-panel">
            <h2>Pesquisas Salvas</h2>
            {savedSearches.length > 0 ? (
              <div className="saved-searches-list">
                {savedSearches.map((search) => (
                  <div key={search._id} className="saved-search-item">
                    <div>
                      <h4>{search.savedName}</h4>
                      <p className="query">Busca: {search.query}</p>
                      <p className="description">{search.savedDescription}</p>
                    </div>
                    <button
                      onClick={() => handleGlobalSearch(search.query)}
                      className="btn-small"
                    >
                      Buscar
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="empty-message">Nenhuma pesquisa salva</p>
            )}
          </div>
        )}

        {/* Aba de Trending */}
        {activeTab === "trending" && (
          <div className="trending-panel">
            <h2>🔥 Pesquisas em Trending</h2>
            {trendingSearches.length > 0 ? (
              <div className="trending-list">
                {trendingSearches.map((trend, idx) => (
                  <div
                    key={idx}
                    className="trending-item"
                    onClick={() => handleGlobalSearch(trend._id)}
                  >
                    <span className="rank">#{idx + 1}</span>
                    <span className="query">{trend._id}</span>
                    <span className="count">{trend.count} buscas</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="empty-message">Carregando trending...</p>
            )}
          </div>
        )}

        {/* Aba de Análise */}
        {activeTab === "analytics" && (
          <SearchAnalytics stats={userStats} />
        )}
      </div>
    </div>
  );
};

export default SearchFeed;
